// The one open row action on All accounts (go-live): a strip above the table, not a row inside it,
// so the table keeps the board's own frame and never widens for an input. It names the account,
// then asks for what that action needs: a strategist for Route to… and Request routing, and a
// reason for either request (the server answers 400 without one).

import { useState } from 'react';
import type { StrategistOption } from '../api/types';
import { ACTION_LABEL, actionNeedsReason, actionNeedsStrategist } from './allAccountsActions';
import type { RowActionKind } from './allAccountsActions';
import { MAX_REQUEST_REASON_LENGTH, REQUEST_REASON_LABEL, REQUEST_REASON_PLACEHOLDER, requestReason } from './requestReason';
import { displayAccountName } from '../lib/format';

export interface ActionSubmission {
  owner: string;
  reason: string;
}

export function AllAccountsActionForm({
  accountName, kind, strategists, strategistsLoading, busy, onSend, onCancel,
}: {
  accountName: string;
  kind: RowActionKind;
  strategists: StrategistOption[];
  strategistsLoading: boolean;
  busy: boolean;
  onSend: (submission: ActionSubmission) => void;
  onCancel: () => void;
}) {
  const [owner, setOwner] = useState('');
  const [why, setWhy] = useState('');
  const needsStrategist = actionNeedsStrategist(kind);
  const needsReason = actionNeedsReason(kind);
  const reason = requestReason(why);
  const ready = (!needsStrategist || Boolean(owner)) && (!needsReason || Boolean(reason));

  return (
    <form
      className="aaactionform"
      aria-label={`${ACTION_LABEL[kind]} ${accountName}`}
      onSubmit={event => {
        event.preventDefault();
        if (ready) onSend({ owner, reason: reason || '' });
      }}
    >
      <span className="aaactiontitle">
        {ACTION_LABEL[kind].replace('…', '')} <b>{displayAccountName(accountName)}</b>
      </span>
      {needsStrategist ? (
        <select aria-label="Strategist" value={owner} disabled={busy || strategistsLoading}
          onChange={event => setOwner(event.target.value)}>
          <option value="">{strategistsLoading ? 'Loading strategists…' : 'Choose a strategist…'}</option>
          {strategists.map(row => <option key={row.email} value={row.email}>{row.name}</option>)}
        </select>
      ) : null}
      {needsReason ? (
        <input
          type="text"
          aria-label={REQUEST_REASON_LABEL}
          placeholder={`${REQUEST_REASON_LABEL} ${REQUEST_REASON_PLACEHOLDER}`}
          maxLength={MAX_REQUEST_REASON_LENGTH}
          value={why}
          disabled={busy}
          autoFocus={!needsStrategist}
          onChange={event => setWhy(event.target.value)}
        />
      ) : null}
      <button type="submit" className="btn primary" disabled={busy || !ready}>
        {busy ? 'Sending…' : kind === 'route' ? 'Route' : 'Send request'}
      </button>
      <button type="button" className="btn" disabled={busy} onClick={onCancel}>Cancel</button>
    </form>
  );
}
